import React, { useState, useEffect } from 'react';
import { TextField, TextFieldProps, InputAdornment, IconButton, Popover } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { parse, format, isValid } from 'date-fns';

interface DateInputProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value: string; // Formato: YYYY-MM-DD (para compatibilidade com backend) ou vazio
  onChange: (value: string) => void; // Retorna YYYY-MM-DD ou string vazia
  displayFormat?: string; // Formato de exibição (padrão: dd/MM/yyyy)
  disablePast?: boolean; // Se true, não permite datas passadas (padrão: false)
  errorMessage?: string; // Mensagem de erro customizada
}

/**
 * Componente de input de data que sempre exibe no formato brasileiro (dd/MM/yyyy)
 * mas mantém o valor interno no formato ISO (YYYY-MM-DD) para compatibilidade com backend
 * Inclui botão de calendário e validações similares ao campo de Prazo Estimado do DemandsBoard
 */
const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  displayFormat = 'dd/MM/yyyy',
  disablePast = false,
  errorMessage,
  ...textFieldProps
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLElement | null>(null);

  // Converter valor ISO (YYYY-MM-DD) para formato de exibição (dd/MM/yyyy)
  useEffect(() => {
    if (value && value.trim() !== '') {
      try {
        // Tentar parsear como ISO (YYYY-MM-DD)
        const date = parse(value, 'yyyy-MM-dd', new Date());
        if (isValid(date)) {
          setDisplayValue(format(date, displayFormat));
        } else {
          setDisplayValue('');
        }
      } catch {
        setDisplayValue('');
      }
    } else {
      setDisplayValue('');
    }
  }, [value, displayFormat]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    // Limpar erro ao começar a digitar
    if (error) {
      setError('');
    }

    // Remover caracteres não numéricos exceto barras
    inputValue = inputValue.replace(/[^\d/]/g, '');

    // Remover barras duplicadas consecutivas
    inputValue = inputValue.replace(/\/+/g, '/');

    // Limitar comprimento
    if (inputValue.length > 10) {
      inputValue = inputValue.substring(0, 10);
    }

    // Obter posição do cursor antes da formatação
    const cursorPosition = e.target.selectionStart || 0;

    // Contar barras existentes
    const barCount = (inputValue.match(/\//g) || []).length;
    const firstBarIndex = inputValue.indexOf('/');

    let newValue = inputValue;

    // Adicionar barras automaticamente apenas quando apropriado
    if (barCount === 0 && inputValue.length >= 2) {
      const firstTwo = inputValue.substring(0, 2);
      if (/^\d{2}$/.test(firstTwo)) {
        newValue = inputValue.substring(0, 2) + '/' + inputValue.substring(2);
      }
    }

    if (barCount === 1 && firstBarIndex === 2 && newValue.charAt(2) === '/' && newValue.charAt(5) !== '/') {
      const afterFirstBar = newValue.substring(3, 5);
      if (/^\d{2}$/.test(afterFirstBar) && newValue.length >= 5) {
        newValue = newValue.substring(0, 5) + '/' + newValue.substring(5);
      }
    }

    // Garantir que nunca temos mais de 2 barras
    const finalBarCount = (newValue.match(/\//g) || []).length;
    if (finalBarCount > 2) {
      const parts = newValue.split('/');
      if (parts.length > 3) {
        newValue = parts[0] + '/' + parts[1] + '/' + parts.slice(2).join('');
      }
    }

    // Remover barras duplicadas consecutivas novamente
    newValue = newValue.replace(/\/+/g, '/');

    setDisplayValue(newValue);

    // Ajustar posição do cursor após formatação
    setTimeout(() => {
      const input = e.target as HTMLInputElement;
      let newCursorPosition = cursorPosition;

      if (newValue.length > inputValue.length) {
        if (cursorPosition <= 2 && newValue.charAt(2) === '/') {
          newCursorPosition = cursorPosition + 1;
        } else if (cursorPosition <= 5 && newValue.charAt(5) === '/') {
          newCursorPosition = cursorPosition + 1;
        }
      }

      if (newValue.length < inputValue.length && cursorPosition > 0) {
        if (inputValue.charAt(cursorPosition - 1) === '/' && newValue.charAt(cursorPosition - 1) !== '/') {
          newCursorPosition = Math.max(0, cursorPosition - 1);
        }
      }

      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleBlur = () => {
    const value = displayValue.trim();
    if (value && value !== '') {
      // Normalizar o formato removendo barras extras e reorganizando
      let normalizedValue = value;

      // Remover barras duplicadas
      normalizedValue = normalizedValue.replace(/\/+/g, '/');

      // Se há barra mas não está na posição correta, tentar normalizar
      const barCount = (normalizedValue.match(/\//g) || []).length;
      const firstBarIndex = normalizedValue.indexOf('/');

      // Se há apenas uma barra mas não está na posição 2, remover e reconstruir
      if (barCount === 1 && firstBarIndex !== 2) {
        const digitsOnly = normalizedValue.replace(/\//g, '');
        if (digitsOnly.length >= 2) {
          normalizedValue = digitsOnly.substring(0, 2) + '/' + digitsOnly.substring(2);
        }
        if (digitsOnly.length >= 4) {
          normalizedValue = normalizedValue.substring(0, 5) + '/' + normalizedValue.substring(5);
        }
      }

      // Validar formato DD/MM/YYYY
      const parsedDate = parse(normalizedValue, displayFormat, new Date());

      if (!isValid(parsedDate)) {
        const errorMsg = errorMessage || 'Data inválida. Use o formato DD/MM/YYYY';
        setError(errorMsg);
        return;
      }

      // Validar que a data não é passada (se disablePast estiver ativo)
      if (disablePast) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(parsedDate);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          setError('A data não pode ser anterior ao dia atual');
          return;
        }
      }

      // Limpar erro se passou na validação
      setError('');
      // Formatar corretamente após validação
      const formattedValue = format(parsedDate, displayFormat);
      setDisplayValue(formattedValue);
      // Converter para formato ISO (YYYY-MM-DD)
      const isoValue = format(parsedDate, 'yyyy-MM-dd');
      onChange(isoValue);
    } else {
      // Limpar erro se campo foi limpo
      setError('');
      onChange('');
    }
  };

  const handleCalendarOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const handleCalendarSelect = (newValue: Date | null) => {
    if (newValue && isValid(newValue)) {
      // Validar que a data não é passada (se disablePast estiver ativo)
      if (disablePast) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(newValue);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          setError('A data não pode ser anterior ao dia atual');
          handleCalendarClose();
          return;
        }
      }

      const formattedValue = format(newValue, displayFormat);
      setDisplayValue(formattedValue);
      const isoValue = format(newValue, 'yyyy-MM-dd');
      onChange(isoValue);
      setError('');
    }
    handleCalendarClose();
  };

  const calendarValue = displayValue && displayValue.length === 10
    ? parse(displayValue, displayFormat, new Date())
    : null;

  return (
    <>
      <TextField
        {...textFieldProps}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={displayFormat.toLowerCase()}
        error={!!error || !!errorMessage}
        helperText={error || errorMessage || textFieldProps.helperText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleCalendarOpen}
                edge="end"
                aria-label="Abrir calendário"
                size="small"
              >
                <CalendarMonth fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          ...textFieldProps.InputProps,
        }}
        inputProps={{
          maxLength: 10,
          pattern: '\\d{2}/\\d{2}/\\d{4}',
          ...textFieldProps.inputProps,
        }}
      />

      <Popover
        open={Boolean(calendarAnchorEl)}
        anchorEl={calendarAnchorEl}
        onClose={handleCalendarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DateCalendar
            value={calendarValue}
            onChange={handleCalendarSelect}
            disablePast={disablePast}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default DateInput;
